import React, { useEffect, useRef, useState } from "react";
import { pdf, Document, Page, Image } from "@react-pdf/renderer";

import { store } from "../../store";
import { apiLabs } from "../../Services/api";
import history from "../../Services/history";
import Denied from "../../Components/Denied";
import Dashboard from "../../Components/Dashboard";
import * as S from "./style";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────
// API pública Labelary: converte ZPL em imagem fiel ao layout original
// (textos, códigos de barras, QR codes, gráficos ^GFA, posicionamento).
const LABELARY_URL = "https://api.labelary.com/v1/printers";

// Etiquetas de marketplace (Meli Full, transportadoras) são 4x6 pol a 203dpi.
const LABEL_CONFIG = { density: 8, width: 4, height: 6 };

// Limite público da Labelary é de 5 req/s — 3 workers com intervalo respeitam isso.
const CONCURRENCY = 3;
const REQUEST_GAP_MS = 250;
const MAX_RETRIES = 2;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
// Separa cada etiqueta ^XA...^XZ presente no arquivo, preservando a ordem.
export function parseZplLabels(content) {
  return content
    .split(/\^XZ/i)
    .map((block) => {
      const start = block.indexOf("^XA");
      return start >= 0 ? `${block.slice(start)}^XZ` : null;
    })
    .filter(Boolean);
}

// Extrai a referência do envio (ex: "Envio: 69719392/1") para exibir no preview.
function extractReference(zpl) {
  const match = zpl.match(/\^FD(Envio:[^^]+)\^FS/i);
  return match ? match[1].trim() : null;
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function renderLabelPng(zpl) {
  const { density, width, height } = LABEL_CONFIG;
  let lastError = null;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const res = await fetch(
        `${LABELARY_URL}/${density}dpmm/labels/${width}x${height}/0/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: zpl,
        }
      );
      if (res.status === 429) {
        // rate limit: aguarda e tenta novamente
        await sleep(1200 * (attempt + 1));
        lastError = new Error("Rate limit");
        continue;
      }
      if (!res.ok) throw new Error(`Labelary HTTP ${res.status}`);
      return await blobToDataURL(await res.blob());
    } catch (err) {
      lastError = err;
      await sleep(600);
    }
  }
  throw lastError;
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────────────────────────────────────
const GenerateLabels = (props) => {
  const {
    location: { idPage },
  } = props;

  const [auth, setAuth] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [labels, setLabels] = useState([]);
  const [rendering, setRendering] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const inputRef = useRef(null);
  const labelsRef = useRef([]);
  const runIdRef = useRef(0);

  useEffect(() => {
    async function verifyAuth() {
      const { profile } = store.getState().user;
      const res = await apiLabs.post("valid_key", {
        id: profile.id,
        menu_id: idPage,
      });
      if (res.data.message === "User not authorized") {
        return setAuth("denied");
      }
      if (res.data.message === "User authorized") {
        return setAuth("authorized");
      }
      return history.push({ pathname: "/", state: { from: props.location } });
    }
    verifyAuth();
  }, [idPage, props.location]);

  // cancela renderizações pendentes ao sair da página
  useEffect(() => () => runIdRef.current++, []);

  // Renderiza (com pool de workers) os índices informados.
  async function startRendering(indices) {
    const runId = ++runIdRef.current;
    setRendering(true);

    let cursor = 0;
    async function worker() {
      while (true) {
        if (runId !== runIdRef.current) return;
        const queuePos = cursor++;
        if (queuePos >= indices.length) return;
        const index = indices[queuePos];
        try {
          const png = await renderLabelPng(labelsRef.current[index].zpl);
          if (runId !== runIdRef.current) return;
          labelsRef.current[index] = {
            ...labelsRef.current[index],
            png,
            status: "done",
          };
        } catch (err) {
          if (runId !== runIdRef.current) return;
          labelsRef.current[index] = {
            ...labelsRef.current[index],
            status: "error",
          };
        }
        setLabels([...labelsRef.current]);
        await sleep(REQUEST_GAP_MS);
      }
    }

    const workers = Array.from(
      { length: Math.min(CONCURRENCY, indices.length) },
      worker
    );
    await Promise.all(workers);
    if (runId === runIdRef.current) setRendering(false);
  }

  async function handleFile(file) {
    setError(null);
    if (!file) return;
    if (!/\.txt$/i.test(file.name)) {
      setError("Formato inválido. Selecione um arquivo com extensão .txt.");
      return;
    }

    let text;
    try {
      text = await readFileAsText(file);
    } catch {
      setError("Não foi possível ler o arquivo. Tente novamente.");
      return;
    }

    const parsed = parseZplLabels(text);
    if (!parsed.length) {
      setError(
        "Nenhuma etiqueta ZPL (^XA ... ^XZ) foi encontrada no arquivo enviado."
      );
      return;
    }

    const initial = parsed.map((zpl, i) => ({
      zpl,
      ref: extractReference(zpl),
      png: null,
      status: "pending",
      order: i,
    }));

    labelsRef.current = initial;
    setFileName(file.name);
    setLabels(initial);
    startRendering(initial.map((_, i) => i));
  }

  function retryFailed() {
    const failed = labelsRef.current
      .map((label, i) => (label.status === "error" ? i : null))
      .filter((i) => i !== null);
    if (!failed.length) return;
    labelsRef.current = labelsRef.current.map((label) =>
      label.status === "error" ? { ...label, status: "pending" } : label
    );
    setLabels([...labelsRef.current]);
    startRendering(failed);
  }

  function reset() {
    runIdRef.current++;
    labelsRef.current = [];
    setLabels([]);
    setFileName(null);
    setError(null);
    setRendering(false);
    if (inputRef.current) inputRef.current.value = "";
  }

  async function generatePdf() {
    if (!allDone || generating) return;
    setGenerating(true);
    setError(null);
    try {
      const { width, height } = LABEL_CONFIG;
      // 1 pol = 72pt — mantém o tamanho físico original da etiqueta no PDF.
      const doc = (
        <Document>
          {labelsRef.current.map((label, i) => (
            <Page
              key={i}
              size={{ width: width * 72, height: height * 72 }}
              style={{ padding: 0 }}
            >
              <Image
                src={label.png}
                style={{ width: "100%", height: "100%" }}
              />
            </Page>
          ))}
        </Document>
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${fileName.replace(/\.txt$/i, "")}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("Falha ao gerar o PDF. Tente novamente.");
    } finally {
      setGenerating(false);
    }
  }

  const total = labels.length;
  const doneCount = labels.filter((l) => l.status === "done").length;
  const errorCount = labels.filter((l) => l.status === "error").length;
  const allDone = total > 0 && doneCount === total;
  const progressPct = total ? Math.round(((doneCount + errorCount) / total) * 100) : 0;
  const aspectRatio = LABEL_CONFIG.height / LABEL_CONFIG.width;

  return (
    <Dashboard title="Gerar Etiquetas">
      {auth === "denied" && <Denied />}
      {auth === "authorized" && (
        <div style={S.page}>
          <style>{S.globalCSS}</style>
          <div style={S.dash}>
            {/* Header */}
            <div style={S.header.wrap}>
              <div>
                <h1 style={S.header.title}>Gerar Etiquetas</h1>
                <div style={S.header.subtitle}>
                  Envie um arquivo .txt com etiquetas ZPL e gere um PDF único
                  pronto para impressão.
                </div>
              </div>
            </div>

            {/* Upload */}
            <div style={S.card}>
              <input
                ref={inputRef}
                type="file"
                accept=".txt,text/plain"
                style={{ display: "none" }}
                onChange={(e) => handleFile(e.target.files[0])}
              />
              <div
                style={S.uploadBox(dragOver)}
                onClick={() => inputRef.current && inputRef.current.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  handleFile(e.dataTransfer.files[0]);
                }}
              >
                <div style={S.uploadTitle}>
                  Arraste o arquivo aqui ou clique para selecionar
                </div>
                <div style={S.uploadHint}>
                  Arquivo .txt contendo uma ou várias etiquetas ZPL (^XA ... ^XZ)
                </div>
              </div>

              {error && (
                <div style={{ ...S.alert("error"), marginTop: 16 }}>{error}</div>
              )}

              {fileName && (
                <div style={S.fileRow}>
                  <div style={S.fileInfo}>
                    <span>{fileName}</span>
                    <span style={S.fileBadge}>
                      {total} etiqueta{total > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button style={S.button("ghost")} onClick={reset}>
                      Limpar
                    </button>
                    {errorCount > 0 && !rendering && (
                      <button style={S.button("danger")} onClick={retryFailed}>
                        Tentar novamente ({errorCount})
                      </button>
                    )}
                    <button
                      style={S.button("primary", !allDone || generating)}
                      disabled={!allDone || generating}
                      onClick={generatePdf}
                    >
                      {generating ? "Gerando PDF..." : "Gerar PDF"}
                    </button>
                  </div>
                </div>
              )}

              {total > 0 && (rendering || !allDone) && (
                <div style={S.progressWrap}>
                  <div style={S.progressLabel}>
                    <span>
                      {rendering
                        ? "Processando etiquetas..."
                        : errorCount > 0
                        ? `${errorCount} etiqueta(s) com falha na renderização`
                        : "Concluído"}
                    </span>
                    <span>
                      {doneCount}/{total}
                    </span>
                  </div>
                  <div style={S.progressTrack}>
                    <div style={S.progressFill(progressPct, errorCount > 0)} />
                  </div>
                </div>
              )}
            </div>

            {/* Preview */}
            {total > 0 && (
              <div style={S.card}>
                <div style={S.previewHeader}>
                  <h2 style={S.previewTitle}>Pré-visualização</h2>
                  <span style={{ fontSize: 13, color: S.colors.textSoft }}>
                    As etiquetas serão incluídas no PDF nesta mesma ordem
                  </span>
                </div>
                <div style={S.grid}>
                  {labels.map((label, i) => (
                    <div key={i} style={S.thumb.wrap}>
                      <div style={S.thumb.imgBox(aspectRatio)}>
                        {label.status === "done" ? (
                          <img
                            src={label.png}
                            alt={`Etiqueta ${i + 1}`}
                            style={S.thumb.img}
                            loading="lazy"
                          />
                        ) : (
                          <div
                            style={S.thumb.placeholder(label.status === "error")}
                          >
                            {label.status === "error"
                              ? "FALHA"
                              : "RENDERIZANDO..."}
                          </div>
                        )}
                      </div>
                      <div style={S.thumb.caption}>
                        <span style={S.thumb.index}>
                          #{String(i + 1).padStart(2, "0")}
                        </span>
                        {label.ref && <span style={S.thumb.ref}>{label.ref}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Dashboard>
  );
};

export default GenerateLabels;
